<script setup lang="ts">
/**
 * #690: Operations → Memory. The enterprise's own stored knowledge: a constellation of what its memory store
 * holds, a list of every note with the evidence behind it, and Ask, Train and (where the caller holds the grant)
 * correction/exclusion reachable from the page itself.
 *
 * The constellation and the list are two reads under the same filters. The constellation is the store's own
 * units and links (`GET /memory/constellation`); a unit opens the note it came from. The list is the notes
 * themselves (`GET /memory`), and is what every reader can use: it also holds the notes the store no longer keeps,
 * excluded and superseded ones.
 *
 * Two boundaries this page is careful about:
 * - Team labels organise knowledge, they do not fence it off. All teams share enterprise memory (#685's agreed
 *   boundary), so the filter says "shared" out loud rather than implying a team has storage of its own.
 * - A link for similar meaning or closeness in time, like a `similar-to` edge, means that and nothing more. It
 *   is never drawn or described as evidence that one entry caused or confirms the other.
 *
 * #1236: every surface, control, heading and empty state is the kit's, and so are the constellation, the list and
 * the note detail (`ZhKnowledgeConstellation`, `ZhKnowledgeGraph`, `ZhKnowledgeDetail`). The screen keeps only
 * what is its own: the filters, the reads, and its sentences.
 */

import type {
    MemoryConstellationResponse,
    MemoryCorrectionState,
    MemoryEvalRunSummary,
    MemoryGraphEdge,
    MemoryGraphQuery,
    MemoryKnowledgeType,
    MemoryLabel,
    MemoryRelationshipType,
} from '@zerohuman/shared'
import {
    Button,
    Checkbox,
    Input,
    type KnowledgeNodeView,
    Label,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhEmptyState,
    ZhKnowledgeConstellation,
    ZhKnowledgeDetail,
    ZhKnowledgeGraph,
    ZhPageTitle,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import ListSearchField from '@/components/ListSearchField.vue'
import MemoryEvaluationsTrend from '@/components/MemoryEvaluationsTrend.vue'
import { useListState } from '@/composables/useListState'
import {
    constellationUnits,
    describeConstellation,
    describeMemorySubset,
    describeRecallHealth,
    filterMemoryNodes,
    memoryMoment,
    type RecallHealthLine,
    unitsOfNote,
} from '@/lib/memoryGraph'
import { memoryEvalApi } from '@/memory-eval-api'
import { type MemoryGraphNode, type MemoryGraphResponse, memoryGraphApi } from '@/memory-graph-api'

/** What each knowledge type means, said in the words a reader needs rather than left as a bare enum value. */
const KNOWLEDGE_LABELS: Record<MemoryKnowledgeType, string> = {
    fact: 'Fact — trained in directly',
    observation: 'Observation — recorded from a run',
    preference: 'Preference — a standing instruction',
    correction: 'Correction — supersedes an earlier entry',
    inference: 'Inference — drawn from a pattern, not evidence on its own',
}

/**
 * What each relationship type is called on a relation line, in the list and in the note detail. `similar-to` is
 * named only here: what it does and does not claim is said once on the page, by `SIMILARITY_HINT` (#1766, PRD L6).
 */
const RELATIONSHIP_LABELS: Record<MemoryRelationshipType, string> = {
    'shares-task': 'Stored against the same task',
    'shares-team': 'Stored against the same team',
    supersedes: 'Supersedes an earlier revision of the same source',
    'similar-to': 'Similar wording',
}

/**
 * What a `similar-to` relation claims: wording in common, and deliberately nothing more. A similarity is never
 * presented as evidence (#690 AC3), so this is on screen whenever a "Similar wording" line is — once per page,
 * as a hint above the list, rather than repeated on every relation line (#1766).
 */
const SIMILARITY_HINT = 'Similar wording — a co-occurrence signal, not evidence'

/** A correction state is said as it is stored, the way this page has always said it. */
const CORRECTION_STATES: Record<MemoryCorrectionState, string> = {
    active: 'active',
    corrected: 'corrected',
    excluded: 'excluded',
}

/** The knowledge types on offer in the type filter, in the order the filter lists them. */
const KNOWLEDGE_TYPES: { value: MemoryKnowledgeType; name: string }[] = [
    { value: 'fact', name: 'Fact' },
    { value: 'observation', name: 'Observation' },
    { value: 'preference', name: 'Preference' },
    { value: 'correction', name: 'Correction' },
    { value: 'inference', name: 'Inference' },
]

const nodes = ref<MemoryGraphNode[]>([])
const edges = ref<MemoryGraphEdge[]>([])
/** The store's own units and links under the same filters. Null until read, and when that read failed. */
const constellation = ref<MemoryConstellationResponse | null>(null)
const total = ref(0)
const loading = ref(true)
const failure = ref('')
const search = ref('')
const teamId = ref('')
const projectId = ref('')
const knowledgeType = ref<MemoryKnowledgeType | ''>('')
const recordedFrom = ref('')
const recordedTo = ref('')
/** Off by default: the page opens on trained notes, and one tick brings in what runs learned (#959). */
const includeRunLearned = ref(false)
/**
 * #633: the search and filters come back on the next visit, in place before the first graph read. Team and project
 * options are labels read from the graph itself, so they are restored as saved; a type the filter no longer offers is
 * dropped.
 */
const list = useListState('memory', {
    search: { ref: search },
    teamId: { ref: teamId },
    projectId: { ref: projectId },
    knowledgeType: { ref: knowledgeType, allowed: () => KNOWLEDGE_TYPES.map(type => type.value) },
    recordedFrom: { ref: recordedFrom },
    recordedTo: { ref: recordedTo },
    includeRunLearned: { ref: includeRunLearned },
})
const selected = ref<MemoryGraphNode | null>(null)
/** How often memory could be read over the last day, as one sentence. Null when unknown or nothing asked. */
const recallHealth = ref<RecallHealthLine | null>(null)

/**
 * The kit Select cannot hold '' as a value, so each "Every …" choice is this stand-in inside its control alone. It
 * maps back to '' here, so the filters — and every query the server is sent — are exactly what they were.
 */
const EVERY = '__every__'

function fromChoice(choice: unknown): string {
    return choice === null || choice === undefined || choice === EVERY ? '' : String(choice)
}

const teamChoice = computed(() => teamId.value || EVERY)
const projectChoice = computed(() => projectId.value || EVERY)
const typeChoice = computed(() => knowledgeType.value || EVERY)

function chooseTeam(choice: unknown): void {
    teamId.value = fromChoice(choice)
}

function chooseProject(choice: unknown): void {
    projectId.value = fromChoice(choice)
}

/** Only a type the filter offers narrows by type; anything else is no type filter at all. */
function chooseType(choice: unknown): void {
    const chosen = fromChoice(choice)
    knowledgeType.value = KNOWLEDGE_TYPES.find(type => type.value === chosen)?.value ?? ''
}

/**
 * A date field says a day, and a day is a window rather than an instant: `from` starts at its first
 * moment and `to` ends at its last, so an entry recorded at noon on the day the reader named is inside the
 * range they asked for rather than just outside it. UTC, matching how every timestamp on this page is read.
 */
function startOfDay(day: string): string | undefined {
    return day === '' ? undefined : `${day}T00:00:00.000Z`
}

function endOfDay(day: string): string | undefined {
    return day === '' ? undefined : `${day}T23:59:59.999Z`
}

/** The filters the reader named. The server narrows the page it returns, and the same filters narrow what is drawn. */
const filters = computed<MemoryGraphQuery>(() => ({
    search: search.value.trim() === '' ? undefined : search.value.trim(),
    teamId: teamId.value === '' ? undefined : teamId.value,
    projectId: projectId.value === '' ? undefined : projectId.value,
    knowledgeType: knowledgeType.value === '' ? undefined : knowledgeType.value,
    from: startOfDay(recordedFrom.value),
    to: endOfDay(recordedTo.value),
    includeRunLearned: includeRunLearned.value ? true : undefined,
}))

/**
 * Whether the last read found memory holding nothing at all, run-learned entries included. Set by `load`, never
 * guessed from `nodes`: the default read lists trained notes only (#959), so an empty page is not an empty memory.
 */
const memoryEmpty = ref(false)

/**
 * Memory holds nothing at all: the read succeeded, and memory, run-learned entries included, has no entry. That is
 * the page's first-run state, not a filter narrowing real entries to zero, which keeps its bare "Nothing matches" line.
 */
const neverLearned = computed(() => !loading.value && !failure.value && memoryEmpty.value)

/** One filtering path for both views, so the chart and the list can never disagree about what matched (#690 AC5). */
const visibleNodes = computed(() => filterMemoryNodes(nodes.value, filters.value))

const visibleIds = computed(() => new Set(visibleNodes.value.map(node => node.id)))

const visibleEdges = computed(() =>
    edges.value.filter(edge => visibleIds.value.has(edge.source) && visibleIds.value.has(edge.target)),
)

/**
 * Whether any relation the list or the detail draws is a similarity, and so whether the page says the similarity
 * hint. Read from the same visible edges they draw, so the hint is there exactly when a "Similar wording" line is.
 */
const showsSimilarity = computed(() => visibleEdges.value.some(edge => edge.type === 'similar-to'))

/** The page's own row behind a note the kit hands back, so an action works on what the server said of it. */
const byId = computed(() => new Map(nodes.value.map(node => [node.id, node])))

function rowOf(note: KnowledgeNodeView): MemoryGraphNode | undefined {
    return byId.value.get(note.id)
}

/**
 * The server saying there is more than this page carries. Usually `total` already says so, but a `search`/type/
 * project filter is decided over built nodes and stops at a scan ceiling, so `total` can be a floor rather than
 * the whole — and then a page carrying every node counted is still a subset, and says so.
 */
const partialCount = ref(false)

/** Whether what is on screen is a subset of what the enterprise holds, and the sentence that says so. */
const subset = computed(() => describeMemorySubset(total.value, visibleNodes.value.length, partialCount.value))

/**
 * Every label the page has seen, kept apart from `nodes` on purpose. `nodes` is the server's answer to the
 * filters that are set *right now*, so reading the team options out of it would let the team filter narrow its
 * own list of teams: pick one team and it becomes the only team on offer, and a reader has to clear the filter
 * before they can switch to another. The same for projects, and for a search or a date range that happens to
 * exclude a team entirely.
 *
 * So a load that narrows nothing replaces the catalogue — it is the one answer carrying everything the
 * (bounded) page holds — and every narrower load only ever adds to it.
 *
 * The response's own `labels` are taken on every load whatever the filters, because the server builds them from
 * the enterprise's teams rather than from the page: a page is capped at its newest entries, so a team whose
 * knowledge is all older would otherwise never reach this catalogue and could never be filtered to at all.
 */
const labelCatalogue = ref<MemoryLabel[]>([])

function labelKey(label: MemoryLabel): string {
    return `${label.kind}:${label.id}`
}

/** Whether the caller named any filter at all, and so whether this load's labels are a subset of the whole. */
function isNarrowed(query: MemoryGraphQuery): boolean {
    return Object.values(query).some(value => value !== undefined && value !== '')
}

function rememberLabels(response: MemoryGraphResponse, narrowed: boolean): void {
    const byKey = new Map<string, MemoryLabel>()
    if (narrowed) {
        for (const label of labelCatalogue.value) byKey.set(labelKey(label), label)
    }
    for (const label of response.labels ?? []) byKey.set(labelKey(label), label)
    for (const node of response.nodes) {
        for (const label of node.labels) byKey.set(labelKey(label), label)
    }
    labelCatalogue.value = [...byKey.values()]
}

/** The labels of one kind the page knows about — a grouping to filter by, not a permission. */
function labelOptions(kind: MemoryLabel['kind']): { id: string; name: string }[] {
    return labelCatalogue.value.filter(label => label.kind === kind).map(label => ({ id: label.id, name: label.name }))
}

/** The teams named by the labels this page has seen — a grouping to filter by, not a permission. */
const teams = computed(() => labelOptions('team'))

/**
 * The projects named by the labels this page has seen. A project is not a memory layer of its own in this OS
 * yet (`MemoryService`'s `filterScope` matches a project by layer id for the same reason), so this offers the
 * projects the stored knowledge actually names and stays out of the way entirely when it names none — rather
 * than an always-empty select implying memory could be filtered by something nothing is labelled with.
 */
const projects = computed(() => labelOptions('project'))

/** The timestamp as stored: UTC, to the minute. A local rendering would move the date for a reader elsewhere. */
function recordedAt(node: MemoryGraphNode): string {
    return memoryMoment(node.createdAt)
}

/** The constellation as the kit draws it. Stable between reads, so the field is only laid out again when memory is. */
const mapUnits = computed(() => constellationUnits(constellation.value?.units ?? []))
const mapLinks = computed(() => constellation.value?.links ?? [])
/** The units of the note being read, ringed on the constellation. */
const mapSelected = computed(() => unitsOfNote(constellation.value?.units ?? [], selected.value?.id ?? null))
/** What to say about the constellation: that it could not be read, or that it is drawn from part of memory. */
const mapNotice = computed(() => describeConstellation(constellation.value))

/**
 * The selected entry as the kit detail reads it. Labels are said by this page (with their kind, and a sentence
 * when there are none), so the kit is given none of its own to draw; evidence falls back to the page's sentence.
 */
const detailNode = computed<KnowledgeNodeView | null>(() => {
    const node = selected.value
    if (!node) return null
    return {
        id: node.id,
        body: node.body,
        knowledgeType: node.knowledgeType,
        correctionState: node.correctionState,
        labels: [],
        provenance: node.provenance ?? 'No source recorded for this entry.',
        recorded: recordedAt(node),
    }
})

/**
 * Which read is the current one. Every filter change starts a read and none of them can be cancelled, so a
 * slower read for a filter the reader has already changed away from can resolve *after* the newer one — and
 * without this its answer would win, leaving the page showing results for a filter it no longer names, with
 * nothing to correct it until the next keystroke. Only the newest read is allowed to write anything.
 */
let loadGeneration = 0

/**
 * Whether memory holds nothing at all, judged from this read. A read that found an entry, or that names a filter
 * beyond the run-learned switch, says nothing about the whole. The default read leaves run-learned entries out
 * (`includeRunLearned` off, #959), so an empty one is checked by the same read with them included: an enterprise
 * whose memory is all learned from runs, the first content most teams get, keeps the bare "Nothing matches" line
 * rather than being told memory is empty. A check that cannot be read claims nothing, so it answers false.
 */
async function holdsNothing(query: MemoryGraphQuery, response: MemoryGraphResponse): Promise<boolean> {
    const { includeRunLearned: runLearned, ...named } = query
    if (response.nodes.length > 0 || isNarrowed(named)) return false
    if (runLearned) return true
    try {
        const everything = await memoryGraphApi.graph({ ...query, includeRunLearned: true })
        return everything.nodes.length === 0
    } catch {
        return false
    }
}

/**
 * The store's own graph under these filters, or null when it could not be read. Its failure is the map's alone:
 * the list comes from the notes index and is still complete, so it never fails the page.
 */
async function readConstellation(query: MemoryGraphQuery): Promise<MemoryConstellationResponse | null> {
    try {
        return await memoryGraphApi.constellation(query)
    } catch {
        return null
    }
}

async function load(): Promise<void> {
    loading.value = true
    const query = filters.value
    loadGeneration += 1
    const generation = loadGeneration
    try {
        const [response, map] = await Promise.all([memoryGraphApi.graph(query), readConstellation(query)])
        if (generation !== loadGeneration) return
        const empty = await holdsNothing(query, response)
        if (generation !== loadGeneration) return
        memoryEmpty.value = empty
        constellation.value = map
        nodes.value = response.nodes
        edges.value = response.edges
        total.value = response.total
        partialCount.value = response.truncated
        rememberLabels(response, isNarrowed(query))
        failure.value = ''
    } catch {
        if (generation !== loadGeneration) return
        failure.value = 'Memory could not be read just now. Nothing here is missing on purpose.'
    } finally {
        if (generation === loadGeneration) loading.value = false
    }
}

/**
 * #694 AC4: the recurring evaluation trend, read once alongside the graph. It is deliberately not tied to the
 * filters above — those narrow which stored entries are drawn, while a run scores the whole fixed question set,
 * so re-reading the trend on every keystroke would cost a request and answer the same thing.
 */
const evalRuns = ref<MemoryEvalRunSummary[]>([])
const evalFailure = ref('')

async function loadEvaluations(): Promise<void> {
    try {
        evalRuns.value = await memoryEvalApi.runs()
        evalFailure.value = ''
    } catch {
        // Said out loud, because an unread trend and a trend with no runs in it look identical otherwise — and
        // one of those means "no evaluation has regressed" while the other means nothing at all.
        evalFailure.value = 'The evaluation trend could not be read just now. This is not a sign that it is empty.'
    }
}

const route = useRoute()

/**
 * Open the note a link names (`/memory?note=<id>`): a chat thread links each note it recalled here. Read by its
 * id, so it opens whatever the filters on the page are showing. A note that is not this enterprise's, or is gone,
 * opens nothing.
 */
async function openLinkedNote(): Promise<void> {
    const id = route.query.note
    if (typeof id !== 'string' || id === '') {
        return
    }
    try {
        const node = await memoryGraphApi.node(id)
        if (node) selected.value = node
    } catch {
        // The link named a note that cannot be read: the page still shows memory, with nothing selected.
    }
}

/**
 * How often memory could be read when a run or a chat reply asked it. A page about memory is where the person
 * who owns it looks, and a recall that fails is otherwise silent: the work carries on without it. Read once on
 * arrival; if it cannot be read, the page says nothing rather than guess.
 */
async function loadRecallHealth(): Promise<void> {
    try {
        recallHealth.value = describeRecallHealth(await memoryGraphApi.recallHealth())
    } catch {
        recallHealth.value = null
    }
}

onMounted(() => {
    void load()
    void openLinkedNote()
    void loadEvaluations()
    void loadRecallHealth()
})

watch(filters, () => {
    void load()
})

function select(node: MemoryGraphNode): void {
    selected.value = node
}

/** The kit says which note was picked by its id; the page opens its own row for it. */
function selectById(id: string): void {
    const node = byId.value.get(id)
    if (node) select(node)
}

/**
 * The constellation says which unit was picked; the page opens the note it came from. The list is one page of
 * notes and the constellation is drawn from more, so a note that is not on the page is read by its id.
 */
async function selectUnit(unitId: string): Promise<void> {
    const noteId = constellation.value?.units.find(unit => unit.id === unitId)?.noteId
    if (!noteId) return
    const listed = byId.value.get(noteId)
    if (listed) {
        select(listed)
        return
    }
    try {
        const node = await memoryGraphApi.node(noteId)
        if (node) select(node)
    } catch {
        // The note behind this unit cannot be read: the page keeps what it had selected.
    }
}

/**
 * Close the detail panel and go back to no selection. Reachable two ways, both without a pointer: the panel's
 * own Close button, and Escape from anywhere on the page — the key a reader already expects to dismiss a panel,
 * handled on the page root so it works while focus is still on the node that opened it.
 */
function dismiss(): void {
    selected.value = null
}

/**
 * Exclude a node from future answers: the tombstone the store already keeps, which stops Ask from drawing on
 * the entry without destroying the evidence that it was ever retained. Only offered for a node the caller holds
 * the grant to correct (`node.correctable`, decided server-side), so nothing here is a client-side permission.
 */
async function exclude(node: MemoryGraphNode): Promise<void> {
    try {
        await memoryGraphApi.exclude(node.id)
        const fresh = await memoryGraphApi.node(node.id)
        if (fresh) selected.value = fresh
        await load()
    } catch {
        failure.value = 'That exclusion was not recorded. The entry is unchanged.'
    }
}
</script>

<template>
    <div class="flex w-full flex-col gap-6 p-6" @keydown.escape="dismiss">
        <header class="flex flex-wrap items-start justify-between gap-4">
            <div class="flex flex-col gap-1">
                <ZhPageTitle>Memory</ZhPageTitle>
                <p class="text-sm text-muted-foreground">
                    What this enterprise has learned, and the evidence behind each entry. Every team shares this
                    knowledge.
                </p>
            </div>
            <div class="flex items-center gap-2">
                <!-- Links, drawn as the kit Button: the RouterLink is the element, the Button its look. -->
                <Button as-child variant="outline" size="sm">
                    <RouterLink data-testid="memory-ask-button" to="/chat">Ask</RouterLink>
                </Button>
                <Button as-child variant="outline" size="sm">
                    <RouterLink data-testid="memory-train-button" to="/chat">Train</RouterLink>
                </Button>
            </div>
        </header>
        <p
            v-if="recallHealth"
            data-testid="memory-recall-health"
            class="text-xs"
            :class="recallHealth.overTarget ? 'text-destructive' : 'text-muted-foreground'"
            :role="recallHealth.overTarget ? 'alert' : undefined"
        >
            {{ recallHealth.label }}
        </p>
        <p class="text-xs text-muted-foreground">
            Ask and Train both run in Chat, against the task a member is working, so every answer cites its
            evidence and every new entry keeps the source event it came from.
        </p>

        <!-- Each Select's id lands on the field it mirrors its choice on; the trigger is named by the same label. -->
        <ZhPanel aria-label="Filter memory" class="flex flex-wrap items-end gap-4">
            <div class="flex flex-col gap-2">
                <Label for="memory-search">Search</Label>
                <ListSearchField
                    id="memory-search"
                    v-model="search"
                    data-testid="memory-search"
                    type="search"
                    placeholder="Search bodies and evidence"
                />
            </div>
            <div class="flex flex-col gap-2" data-testid="memory-team-filter">
                <Label id="memory-team-label" for="memory-team">
                    Team label — memory is shared across the whole enterprise
                </Label>
                <Select id="memory-team" :model-value="teamChoice" @update:model-value="chooseTeam">
                    <SelectTrigger aria-labelledby="memory-team-label">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem :value="EVERY">Every team</SelectItem>
                        <SelectItem v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div v-if="projects.length > 0" class="flex flex-col gap-2" data-testid="memory-project-filter">
                <Label id="memory-project-label" for="memory-project">
                    Project label — also shared across the whole enterprise
                </Label>
                <Select id="memory-project" :model-value="projectChoice" @update:model-value="chooseProject">
                    <SelectTrigger aria-labelledby="memory-project-label">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem :value="EVERY">Every project</SelectItem>
                        <SelectItem v-for="project in projects" :key="project.id" :value="project.id">
                            {{ project.name }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div class="flex flex-col gap-2" data-testid="memory-type-filter">
                <Label id="memory-type-label" for="memory-type">Knowledge type</Label>
                <Select id="memory-type" :model-value="typeChoice" @update:model-value="chooseType">
                    <SelectTrigger aria-labelledby="memory-type-label">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem :value="EVERY">Every type</SelectItem>
                        <SelectItem v-for="type in KNOWLEDGE_TYPES" :key="type.value" :value="type.value">
                            {{ type.name }}
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div class="flex flex-col gap-2" data-testid="memory-time-filter">
                <Label for="memory-from">Recorded from</Label>
                <Input id="memory-from" v-model="recordedFrom" data-testid="memory-from" type="date" />
            </div>
            <div class="flex flex-col gap-2">
                <Label for="memory-to">Recorded until</Label>
                <Input id="memory-to" v-model="recordedTo" data-testid="memory-to" type="date" />
            </div>
            <div class="flex items-center gap-2">
                <Checkbox
                    id="memory-include-run-learned"
                    :model-value="includeRunLearned"
                    data-testid="memory-include-run-learned"
                    @update:model-value="includeRunLearned = $event === true"
                />
                <Label for="memory-include-run-learned">Include what runs learned</Label>
            </div>
            <Button v-if="!list.isDefault.value" type="button" variant="outline" size="sm" @click="list.clear()">Clear filters</Button>
        </ZhPanel>

        <ZhPanel v-if="failure" tone="failed">
            <p data-testid="memory-failure" class="text-sm text-destructive">{{ failure }}</p>
        </ZhPanel>
        <ZhPanel v-if="subset.truncated">
            <p data-testid="memory-subset-notice" class="text-sm text-muted-foreground">
                {{ subset.label }} entries. Narrow the search or filters to see the rest.
            </p>
        </ZhPanel>

        <div class="grid gap-6 lg:grid-cols-3">
            <ZhPanel aria-label="Memory map" class="flex flex-col gap-4 lg:col-span-2">
                <ZhKnowledgeConstellation
                    v-if="mapUnits.length > 0"
                    data-testid="memory-map"
                    label="Memory map"
                    :units="mapUnits"
                    :links="mapLinks"
                    :selected-ids="mapSelected"
                    @select="selectUnit"
                />
                <!-- Nothing learned at all, with no filter named: say what will appear and where it comes from. -->
                <ZhEmptyState v-else-if="neverLearned" data-testid="memory-first-run">
                    Nothing is in memory yet. What your team learns from runs appears here, with the evidence behind
                    each entry.
                    <template #action>
                        <Button as-child size="sm"><RouterLink to="/tasks">Go to Tasks</RouterLink></Button>
                    </template>
                </ZhEmptyState>
                <ZhEmptyState v-else-if="!loading && !mapNotice.unavailable">
                    Nothing matches these filters yet.
                </ZhEmptyState>
                <!-- An empty memory has no map to qualify: the first-run state above already says what is here. -->
                <template v-if="!loading && !neverLearned">
                    <p
                        v-for="line in mapNotice.lines"
                        :key="line"
                        data-testid="memory-map-notice"
                        :class="mapNotice.unavailable ? 'text-sm text-destructive' : 'text-xs text-muted-foreground'"
                    >
                        {{ line }}
                    </p>
                </template>
            </ZhPanel>

            <ZhPanel v-if="selected && detailNode" as="aside" data-testid="memory-node-detail" class="flex flex-col gap-3">
                <div class="flex justify-end">
                    <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        data-testid="memory-detail-dismiss"
                        aria-label="Close node detail"
                        @click="dismiss"
                    >
                        Close
                    </Button>
                </div>
                <ZhKnowledgeDetail
                    :node="detailNode"
                    :nodes="visibleNodes"
                    :edges="visibleEdges"
                    :type-words="KNOWLEDGE_LABELS"
                    :state-words="CORRECTION_STATES"
                    :relation-words="RELATIONSHIP_LABELS"
                >
                    <template #facts>
                        <div>
                            <dt>Stored at</dt>
                            <dd>{{ selected.layer }} · {{ selected.layerId }}</dd>
                        </div>
                        <div>
                            <dt>Labels</dt>
                            <dd v-if="selected.labels.length > 0">
                                {{ selected.labels.map(label => `${label.name} (${label.kind})`).join(', ') }}
                            </dd>
                            <dd v-else>No team, project or task label.</dd>
                        </div>
                    </template>
                </ZhKnowledgeDetail>
                <template v-if="selected.correctable">
                    <p class="text-xs text-muted-foreground">
                        You hold the grant to correct this entry: excluding it stops Ask drawing on it, and keeps the
                        record that it was retained.
                    </p>
                    <div>
                        <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            data-testid="memory-correct-button"
                            @click="exclude(selected)"
                        >
                            Correct or exclude
                        </Button>
                    </div>
                </template>
                <p v-else class="text-xs text-muted-foreground">
                    Correcting this entry needs a grant this session does not hold.
                </p>
            </ZhPanel>
        </div>

        <!-- Nothing learned yet: the chart's first-run state already says so, so there is no list to draw. -->
        <ZhPanel v-if="!neverLearned" aria-label="Knowledge list" class="flex flex-col gap-3">
            <ZhSectionHeading>All matching entries</ZhSectionHeading>
            <!-- Said once, before the rows, rather than on every "Similar wording" line (#1766). -->
            <p v-if="showsSimilarity" data-testid="memory-similarity-hint" class="text-xs text-muted-foreground">
                {{ SIMILARITY_HINT }}
            </p>
            <ZhKnowledgeGraph
                v-if="visibleNodes.length > 0"
                label="All matching entries"
                :nodes="visibleNodes"
                :edges="visibleEdges"
                :selected-id="selected?.id ?? null"
                :type-words="KNOWLEDGE_LABELS"
                :relation-words="RELATIONSHIP_LABELS"
                activator-test-id="memory-list-row"
                @select="selectById"
            >
                <!-- Beside each entry, as it has always been listed: its correction state and when it was recorded. -->
                <template #actions="{ node: note }">
                    <span v-if="rowOf(note)" class="text-xs text-muted-foreground">
                        {{ CORRECTION_STATES[note.correctionState] }} · {{ recordedAt(rowOf(note) as MemoryGraphNode) }}
                    </span>
                </template>
            </ZhKnowledgeGraph>
            <ZhEmptyState v-else-if="!loading">Nothing matches these filters yet.</ZhEmptyState>
        </ZhPanel>

        <ZhPanel v-if="evalFailure" tone="failed">
            <p data-testid="memory-eval-failure" class="text-sm text-destructive">{{ evalFailure }}</p>
        </ZhPanel>
        <MemoryEvaluationsTrend :runs="evalRuns" />
    </div>
</template>
